/**
 * workspaceSession  —  Phase 77
 *
 * Tiny `sessionStorage`-backed gate flag that distinguishes a freshly
 * authenticated user (who must be routed to the Workspaces portal to
 * EXPLICITLY pick their active company) from an established session that
 * has already made that choice (and should be allowed to reload into the
 * cached active company).
 *
 * ─── Why sessionStorage (not localStorage)? ──────────────────────────────
 *   • localStorage persists across tabs AND across browser restarts — that
 *     would defeat the directive: fresh logins must always land on the
 *     portal, regardless of whether `axelio_active_company_id` is cached.
 *   • sessionStorage is per-tab and dies when the tab closes, so a
 *     re-authentication in a new tab/window naturally starts un-flagged
 *     and is forced through the portal.
 *   • Within an established session (page reload, internal navigation,
 *     `storage` cross-tab sync of the `axelio_active_company_id` key) the
 *     flag stays set so the user is NOT bounced back to the portal.
 *
 * ─── Lifecycle ────────────────────────────────────────────────────────────
 *   • SET   — every explicit "I want company X as active" interaction:
 *               – WorkspacesPage card click
 *               – ModuleGuard auto-switch fallback
 *               – UnauthorizedModulePage company picker
 *               – Onboarding completion (first company created)
 *               – Invite acceptance (JoinPage)
 *             The Phase 76 context's `setActiveCompanyId` setter is the
 *             central choke point used by all of these surfaces, so we
 *             call `markWorkspaceSelected()` once from inside it.
 *   • CLEAR — `signOut()` and any session reset path.
 *
 * The flag is intentionally NOT keyed by user id — sessionStorage already
 * scopes it to the tab/session, and clearing it on signOut covers the
 * "different user on the same tab" case.
 */

const FLAG_KEY = 'axelio_session_explicit_company';

/**
 * Returns true when the current tab has been flagged as "the user has
 * already chosen their active company in this session". The Phase 77 gate
 * uses this to allow direct entry into `/app/*`; absence of the flag
 * forces a redirect to `/workspaces`.
 */
export function hasWorkspaceSelection(): boolean {
  try {
    return sessionStorage.getItem(FLAG_KEY) === '1';
  } catch {
    // Private mode / quota — be permissive so the user is never stuck on
    // an unreachable portal page. Server-side guards (RLS) remain the
    // ultimate authorization line.
    return true;
  }
}

/** Marks the current tab as having an explicitly-chosen active company. */
export function markWorkspaceSelected(): void {
  try {
    sessionStorage.setItem(FLAG_KEY, '1');
  } catch {
    /* ignore quota / privacy errors */
  }
}

/** Clears the flag (sign-out, session reset). */
export function clearWorkspaceSelection(): void {
  try {
    sessionStorage.removeItem(FLAG_KEY);
  } catch {
    /* ignore */
  }
}
